import { useState } from "react";
import axios from "axios";
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
    Radio, RadioGroup, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import { apiConstants } from "../../common/constants";
import { FONT, WEIGHT } from "../../styles/foundation/typography";
import { BORDER, SUBTLE } from "../../styles/foundation/colors";
import { gradientBtnSx } from "./ticketFormStyles";

// Mirrors EscalationExportField on the backend (keep the names in sync). The Prozo-only columns
// (severity, assignee, tags, visibility) aren't offered here, and the server drops them anyway.
const EXPORT_FIELDS = [
    { name: "TICKET_NUMBER", label: "Ticket #", defaultChecked: true },
    { name: "STATUS", label: "Status", defaultChecked: true },
    { name: "TICKET_STAGE", label: "Ticket Stage (Open/Pending/Closed)", defaultChecked: true },
    { name: "CATEGORY", label: "Category", defaultChecked: true },
    { name: "SUB_CATEGORY", label: "Sub Category", defaultChecked: true },
    { name: "AWB_NUMBERS", label: "AWB", defaultChecked: true },
    { name: "COURIER", label: "Courier", defaultChecked: false },
    { name: "SHIPMENT_STATUS", label: "Shipment Status", defaultChecked: false },
    { name: "DESCRIPTION", label: "Description", defaultChecked: true },
    { name: "CREATED_BY", label: "Created By", defaultChecked: false },
    { name: "REQUESTER", label: "Requester", defaultChecked: false },
    { name: "CREATED_AT", label: "Created", defaultChecked: true },
    { name: "UPDATED_AT", label: "Last Updated", defaultChecked: false },
    { name: "FIRST_RESPONSE_DUE_AT", label: "First Response Due", defaultChecked: false },
    { name: "RESOLUTION_DUE_AT", label: "Resolution Due By", defaultChecked: false },
    { name: "RESOLVED_AT", label: "Resolved At", defaultChecked: false },
    { name: "CLOSED_AT", label: "Closed At", defaultChecked: false },
    { name: "SLA_STATUS", label: "SLA Status (Breached)", defaultChecked: true },
    { name: "ACKNOWLEDGED", label: "Acknowledged", defaultChecked: true },
    { name: "ACKNOWLEDGED_AT", label: "Acknowledged At", defaultChecked: false },
    { name: "LATEST_COMMENT", label: "Latest Comment (and its timestamp)", defaultChecked: true },
    { name: "COMMENTS", label: "Last 3 Comments (each with its timestamp)", defaultChecked: false },
    { name: "STATUS_MOVEMENT", label: "Status movement (each status with its date & time)", defaultChecked: false },
];

const sectionLabelSx = { fontFamily: FONT, fontSize: 12, color: SUBTLE, fontWeight: WEIGHT.bold, textTransform: "uppercase", letterSpacing: ".04em" };

// responseType 'blob' delivers an error body as a Blob too, so the backend's reason
// (e.g. "over the export limit") has to be read out of it explicitly.
const exportErrorMessage = async (error) => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text());
            if (parsed?.message) return parsed.message;
        } catch (e) {
            // not JSON — fall through
        }
    } else if (data?.message) {
        return data.message;
    }
    return "Could not export tickets";
};

/** Export tickets with the columns the merchant picks: every ticket matching `filters`, or (selection)
 *  the ticked ones. count is how many that is, when known. */
const ExportTicketsDialog = ({ open, onClose, filters, count, selection = false }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [format, setFormat] = useState("EXCEL");
    const [selected, setSelected] = useState(EXPORT_FIELDS.filter((f) => f.defaultChecked).map((f) => f.name));
    const [includeAttachments, setIncludeAttachments] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const allSelected = selected.length === EXPORT_FIELDS.length;
    const toggle = (name) => setSelected((prev) => (prev.includes(name) ? prev.filter((f) => f !== name) : [...prev, name]));
    // Keeps the list's order, whatever order the boxes were ticked in.
    const ordered = EXPORT_FIELDS.map((f) => f.name).filter((n) => selected.includes(n));

    const handleExport = async () => {
        if (ordered.length === 0) {
            enqueueSnackbar("Pick at least one column to export.", { variant: "warning" });
            return;
        }
        setDownloading(true);
        try {
            const response = await axios.post(
                `${apiConstants.BASE_LOCAL}${apiConstants.ESCALATION_EXPORT_V2}`,
                { filters, format, fields: includeAttachments ? [...ordered, "ATTACHMENTS"] : ordered },
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `tickets_${Date.now()}.${format === "EXCEL" ? "xlsx" : "csv"}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 0);
            onClose();
        } catch (error) {
            enqueueSnackbar(await exportErrorMessage(error), { variant: "error" });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !downloading && onClose()} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {selection ? `Download ${count === 1 ? "1 ticket" : `${(count || 0).toLocaleString("en-IN")} tickets`}` : "Export Tickets"}
                <IconButton onClick={onClose} size="small" disabled={downloading}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: SUBTLE }}>
                    {selection ? "Downloads the tickets you selected."
                        : `Exports every ticket matching your current filters${count != null ? ` (${count.toLocaleString("en-IN")})` : ""} — not just the ones on this page.`}
                </Typography>

                <Box>
                    <Typography sx={sectionLabelSx}>Format</Typography>
                    <RadioGroup row value={format} onChange={(e) => setFormat(e.target.value)}>
                        <FormControlLabel value="EXCEL" control={<Radio size="small" />} label="Excel" />
                        <FormControlLabel value="CSV" control={<Radio size="small" />} label="CSV" />
                    </RadioGroup>
                </Box>

                <Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography sx={sectionLabelSx}>Columns ({selected.length} selected)</Typography>
                        <FormControlLabel sx={{ mr: 0 }}
                            control={<Checkbox size="small" checked={allSelected} indeterminate={selected.length > 0 && !allSelected}
                                onChange={() => setSelected(allSelected ? [] : EXPORT_FIELDS.map((f) => f.name))} />}
                            label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Select all</Typography>} />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, maxHeight: 300, overflowY: "auto" }}>
                        {EXPORT_FIELDS.map((f) => (
                            <FormControlLabel key={f.name}
                                control={<Checkbox size="small" checked={selected.includes(f.name)} onChange={() => toggle(f.name)} />}
                                label={<Typography sx={{ fontSize: 13 }}>{f.label}</Typography>} />
                        ))}
                    </Box>
                </Box>

                <Box sx={{ borderTop: `1px solid ${BORDER}`, pt: 1.5 }}>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={includeAttachments} onChange={(e) => setIncludeAttachments(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Include attachment links</Typography>} />
                    <Typography sx={{ fontSize: 12, color: SUBTLE, ml: 4 }}>
                        Adds one column per attachment with a download link. Each download makes fresh links that work for 7 days from then; download again any time for new ones.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={downloading} sx={{ textTransform: "none" }}>Cancel</Button>
                <LoadingButton variant="contained" loading={downloading} onClick={handleExport} sx={gradientBtnSx}>{selection ? "Download" : "Export"}</LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ExportTicketsDialog;
